import React from 'react';
import { TrendingUp, Truck, Package, CheckCircle, Clock, AlertCircle, MapPin, X, ChevronRight, FileText, Download, Settings } from 'lucide-react';
import '../../../../styles/components/loading-dashboard.scss';
import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';

// Feature Flags Manager
class FeatureFlags {
  static DEFAULT_FLAGS = {
    showModal: true,
    showTruckAnimation: true,
    showExportButtons: true,
    showStatusBadges: true,
    showProgressBars: true,
    showLastActivity: true
  };

  static getFlags() {
    const saved = localStorage.getItem('dashboardFeatureFlags');
    return saved ? JSON.parse(saved) : this.DEFAULT_FLAGS;
  }

  static saveFlags(flags) {
    localStorage.setItem('dashboardFeatureFlags', JSON.stringify(flags));
  }

  static resetFlags() {
    localStorage.removeItem('dashboardFeatureFlags');
    return this.DEFAULT_FLAGS;
  }
}

class LoadingProgressDashboard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedSite: null,
      timeRange: 'today',
      hideSidebar: false,
      showFlagSettings: false,
      featureFlags: FeatureFlags.getFlags(),

      // Sample data
      sites: [
        {
          id: 1,
          name: 'RIMA',
          location: 'Rima Auction Yard',
          status: 'in-progress',
          progress: 58.56,
          totalPlanned: 15000,
          totalCollected: 8783.87,
          activeLots: 2,
          lastActivity: '2 hours ago',
          lots: [
            {
              lotRef: 'A1/2024/S15K',
              buyer: 'Muscat Steel Melting Co. LLC',
              quantity: 15000,
              collected: 8783.87,
              progress: 58.56,
              status: 'in-progress',
              yards: [
                {
                  name: 'Rima Auction Yard -01',
                  planned: 3063.16,
                  collected: 3063.16,
                  progress: 100,
                  status: 'completed',
                  collectionDates: '2025-01-12 to 2025-01-24'
                },
                {
                  name: 'Rima Auction Yard -02',
                  planned: 50.74,
                  collected: 50.74,
                  progress: 100,
                  status: 'completed',
                  collectionDates: '2025-01-12'
                },
                {
                  name: 'Nimr Logistic Auction Yard -01',
                  planned: 9500,
                  collected: 5669.97,
                  progress: 59.68,
                  status: 'in-progress',
                  collectionDates: '2025-01-20 to 2025-02-12',
                  due: '8 Days'
                }
              ]
            }
          ]
        },
        {
          id: 2,
          name: 'NIMR',
          location: 'Nimr SOS Auction Yard',
          status: 'completed',
          progress: 100,
          totalPlanned: 13000,
          totalCollected: 13000.09,
          activeLots: 0,
          lastActivity: '1 day ago',
          lots: [
            {
              lotRef: 'A1/2024/OIS13K',
              buyer: 'Billad Maajan Al Asriya LLC',
              quantity: 13000,
              collected: 13000.09,
              progress: 100,
              status: 'completed',
              yards: [
                {
                  name: 'Nimr SOS Auction Yard -03',
                  planned: 13000,
                  collected: 13000.09,
                  progress: 100,
                  status: 'completed',
                  collectionDates: '2025-01-20 to 2025-02-12'
                }
              ]
            }
          ]
        },
        {
          id: 3,
          name: 'YIBAL',
          location: 'Yibal D Station',
          status: 'in-progress',
          progress: 21.72,
          totalPlanned: 10000,
          totalCollected: 2172.14,
          activeLots: 1,
          lastActivity: '4 hours ago',
          lots: [
            {
              lotRef: 'A1/2024/Y10K',
              buyer: 'Muscat Steel Melting Co. LLC',
              quantity: 10000,
              collected: 2172.14,
              progress: 21.72,
              status: 'in-progress',
              yards: [
                {
                  name: 'Yibal D Station Scrap Yard No. 2',
                  planned: 5300,
                  collected: 2172.14,
                  progress: 40.98,
                  status: 'in-progress',
                  collectionDates: '2025-01-23 to 2025-02-28'
                }
              ]
            }
          ]
        },
        {
          id: 4,
          name: 'FAHUD',
          location: 'Fahud Warehouse',
          status: 'in-progress',
          progress: 89.00,
          totalPlanned: 400,
          totalCollected: 355.99,
          activeLots: 1,
          lastActivity: '6 hours ago',
          lots: [
            {
              lotRef: 'A1/2024/F400',
              buyer: 'Jabal Salalah National Trade LLC',
              quantity: 400,
              collected: 355.99,
              progress: 89.00,
              status: 'in-progress',
              yards: [
                {
                  name: 'Fahud Warehouse Cable Yard',
                  planned: 94.28,
                  collected: 94.28,
                  progress: 100,
                  status: 'completed',
                  collectionDates: '2025-01-23 to 2025-01-30',
                  due: '5 Days'
                }
              ]
            }
          ]
        },
        {
          id: 5,
          name: 'MARMUL',
          location: 'Marmul Facility',
          status: 'in-progress',
          progress: 45.3,
          totalPlanned: 8500,
          totalCollected: 3850.5,
          activeLots: 2,
          lastActivity: '3 hours ago',
          lots: []
        },
        {
          id: 6,
          name: 'QARN ALAM',
          location: 'Qarn Alam Site',
          status: 'completed',
          progress: 100,
          totalPlanned: 5200,
          totalCollected: 5200,
          activeLots: 0,
          lastActivity: '2 days ago',
          lots: []
        },
        {
          id: 7,
          name: 'LEKHWAIR',
          location: 'Lekhwair Station',
          status: 'in-progress',
          progress: 67.8,
          totalPlanned: 7800,
          totalCollected: 5288.4,
          activeLots: 1,
          lastActivity: '1 hour ago',
          lots: []
        },
        {
          id: 8,
          name: 'AMAL',
          location: 'Amal Processing',
          status: 'in-progress',
          progress: 34.2,
          totalPlanned: 6000,
          totalCollected: 2052,
          activeLots: 1,
          lastActivity: '5 hours ago',
          lots: []
        }
      ]
    };
  }

  getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} />;
      case 'in-progress': return <Clock size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  toggleFeatureFlag = (flagName) => {
    this.setState(prevState => {
      const newFlags = {
        ...prevState.featureFlags,
        [flagName]: !prevState.featureFlags[flagName]
      };
      FeatureFlags.saveFlags(newFlags);
      return { featureFlags: newFlags };
    });
  };

  resetFeatureFlags = () => {
    const defaultFlags = FeatureFlags.resetFlags();
    this.setState({ featureFlags: defaultFlags });
  };

  render() {
    const { sites, selectedSite, timeRange, hideSidebar, showFlagSettings, featureFlags } = this.state;

    const totalPlanned = sites.reduce((sum, site) => sum + site.totalPlanned, 0);
    const totalCollected = sites.reduce((sum, site) => sum + site.totalCollected, 0);
    const overallProgress = (totalCollected / totalPlanned) * 100;
    const activeSites = sites.filter(site => site.status === 'in-progress').length;

    return (
      <React.Fragment>
        <HeaderBanner
          title="Loading Progress Dashboard"
          subtitle="Real-time monitoring of auction collection status and progress"
        />

        <section className="mt-5 site-visit-details-page">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!hideSidebar && <SideBar />}
              </div>

              <div className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}>
                <div className="loading-dashboard">
                  <div className="dashboard-container">
                    <div className="dashboard-wrapper">

                      {/* Header */}
                      <div className="dashboard-header">
                        <div className="header-content">
                          <div className="header-title-section">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <span

                                onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                              >
                                {hideSidebar ? <i className="icon-size-actual"></i> : <i className="icon-size-fullscreen"></i>}
                              </span>
                              <div>
                                <h1 className="main-title">Loading Progress Dashboard</h1>
                                <p className="main-subtitle">Real-time monitoring of auction collection status</p>
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <select
                              value={timeRange}
                              onChange={(e) => this.setState({ timeRange: e.target.value })}
                              className="button1 button1-gray"
                            >
                              <option value="today">Today</option>
                              <option value="week">This Week</option>
                              <option value="month">This Month</option>
                            </select>
                            <button
                              onClick={() => this.setState({ showFlagSettings: !showFlagSettings })}
                              className="button1 button1-primary"
                            >
                              <Settings size={20} />
                            </button>
                          </div>
                        </div>

                        {/* Summary Stats */}
                        <div className="client-cards-container-new">
                          <div className="client-amount-card-new">
                            <Package size={32} className="card-heade-newr" />
                            <div className="card-title-new">Total Sites</div>
                            <div className="card-amount-new" style={{fontSize:"20px"}}>{sites.length}</div>
                          </div>

                          <div className="client-amount-card-new">
                            <TrendingUp size={32} className="card-heade-newr" />
                            <div className="card-title-new">Total Quantity (KG)</div>
                            <div className="card-amount-new" style={{fontSize:"20px"}}>{(totalPlanned / 1000).toFixed(1)}K</div>
                          </div>

                          <div className="client-amount-card-new">
                            <div className='card-title-new'></div>
                            <CheckCircle size={32} className="card-heade-newr" />
                            <div className="card-title-new">Overall Progress</div>
                            <div className="card-amount-new" style={{fontSize:"20px"}}>{overallProgress.toFixed(1)}%</div>
                          </div>

                          <div className="client-amount-card-new">
                            <Truck size={32} className="card-heade-newr" />
                            <div className="card-title-new">Active Sites</div>
                            <div className="card-amount-new" style={{fontSize:"20px"}}>{activeSites}</div>
                          </div>
                        </div>
                      </div>

                      {/* Sites Grid */}
                      <div className="sites-grid">
                        {sites.map(site => (
                          <div
                            key={site.id}
                            onClick={() => featureFlags.showModal && this.setState({ selectedSite: site })}
                            className="site-card"
                            style={{ cursor: featureFlags.showModal ? 'pointer' : 'default' }}
                          >
                            <div className={`status-indicator ${site.status}`} />

                            <div className="site-card-header">
                              <div>
                                <h3 className="site-name">{site.name}</h3>
                                <div className="site-location">
                                  <MapPin size={14} />
                                  <span>{site.location}</span>
                                </div>
                              </div>
                              {featureFlags.showStatusBadges && (
                                <div className={`status-badge ${site.status}`}>
                                  {this.getStatusIcon(site.status)}
                                  {site.status === 'completed' ? 'Completed' : 'In Progress'}
                                </div>
                              )}
                            </div>

                            {/* Truck Loading Visual */}
                            {featureFlags.showTruckAnimation && (
                              <div className="truck-container">
                                <svg viewBox="0 0 300 120" className="truck-svg">
                                  <g className="truck">
                                    <rect x="10" y="60" width="40" height="35" fill="#2c3e50" rx="3" />
                                    <rect x="15" y="65" width="15" height="15" fill="#3498db" opacity="0.7" />
                                    <rect x="35" y="65" width="10" height="15" fill="#3498db" opacity="0.7" />
                                    <rect x="55" y="50" width="150" height="45" fill="#1b8f9b" rx="3" />
                                    <rect x="55" y="50" width={150 * (site.progress / 100)} height="45" fill="#10b981" rx="3" />
                                    <line x1="75" y1="50" x2="75" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                    <line x1="105" y1="50" x2="105" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                    <line x1="135" y1="50" x2="135" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                    <line x1="165" y1="50" x2="165" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                    <line x1="195" y1="50" x2="195" y2="95" stroke="#fff" strokeWidth="1" opacity="0.3" />
                                    <circle cx="35" cy="100" r="8" fill="#2c3e50" />
                                    <circle cx="35" cy="100" r="5" fill="#7f8c8d" />
                                    <circle cx="85" cy="100" r="8" fill="#2c3e50" />
                                    <circle cx="85" cy="100" r="5" fill="#7f8c8d" />
                                    <circle cx="175" cy="100" r="8" fill="#2c3e50" />
                                    <circle cx="175" cy="100" r="5" fill="#7f8c8d" />
                                  </g>
                                  <text x="130" y="77" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="bold">
                                    {site.progress.toFixed(0)}%
                                  </text>
                                </svg>
                              </div>
                            )}

                            {/* Site Info */}
                            <div className="site-info-grid">
                              <div className="info-item">
                                <div className="info-label">Collected</div>
                                <div className="d-flex align-items-center">
                                  <div className="info-value">{site.totalCollected.toLocaleString()}</div>
                                  <div className="info-unit">tons</div>
                                </div>
                              </div>
                              <div className="info-item">
                                <div className="info-label">Planned</div>
                                <div className="d-flex align-items-center">
                                  <div className="info-value">{site.totalPlanned.toLocaleString()}</div>
                                  <div className="info-unit">tons</div>
                                </div>
                              </div>
                            </div>

                            {/* Footer Info */}
                            <div className="site-card-footer">
                              {featureFlags.showLastActivity && (
                                <div className="last-activity">
                                  <Clock size={14} />
                                  <span>{site.lastActivity}</span>
                                </div>
                              )}
                              {featureFlags.showModal && (
                                <div className="view-details">
                                  View Details
                                  <ChevronRight size={16} />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Detail Modal */}
                      {featureFlags.showModal && selectedSite && (
                        <div className="modal-overlay" onClick={() => this.setState({ selectedSite: null })}>
                          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header-loading">
                              <div className="modal-header-content">
                                <div>
                                  <h2 className="modal-title">{selectedSite.name}</h2>
                                  <div className="modal-location">
                                    <MapPin size={16} />
                                    <span>{selectedSite.location}</span>
                                  </div>
                                </div>
                                <button onClick={() => this.setState({ selectedSite: null })} className="close-button">
                                  <X size={24} />
                                </button>
                              </div>

                              <div className="modal-summary">
                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Progress</div>
                                  <div className="summary-item-value">{selectedSite.progress.toFixed(1)}%</div>
                                </div>
                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Collected</div>
                                  <div className="summary-item-value">{selectedSite.totalCollected.toLocaleString()}</div>
                                </div>
                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Active Lots</div>
                                  <div className="summary-item-value">{selectedSite.activeLots}</div>
                                </div>

                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Pending Tickets</div>
                                  <div className="summary-item-value">{selectedSite.activeLots}</div>
                                </div>

                                <div className="modal-summary-item">
                                  <div className="summary-item-label">Completed Tickets</div>
                                  <div className="summary-item-value">{selectedSite.activeLots}</div>
                                </div>
                              </div>
                            </div>

                            <div className="modal-body">
                              {selectedSite.lots && selectedSite.lots.length > 0 ? (
                                selectedSite.lots.map((lot, lotIndex) => (
                                  <div key={lotIndex} className="lot-section">
                                    <div className="lot-header-card">
                                      <div className="lot-header-content">
                                        <div>
                                          <h3 className="lot-title">Lot {lot.lotRef}</h3>
                                          <p className="lot-buyer">{lot.buyer}</p>
                                        </div>
                                        {featureFlags.showStatusBadges && (
                                          <div className={`status-badge ${lot.status}`}>
                                            {this.getStatusIcon(lot.status)}
                                            {lot.status === 'completed' ? 'Completed' : 'In Progress'}
                                          </div>
                                        )}
                                      </div>

                                      {featureFlags.showProgressBars && (
                                        <div className="lot-progress-bar-container">
                                          <div className="lot-progress-bar">
                                            <div
                                              className="lot-progress-fill"
                                              style={{ width: `${lot.progress}%` }}
                                            />
                                          </div>
                                          <span className="lot-progress-text">{lot.progress.toFixed(1)}%</span>
                                        </div>
                                      )}

                                      <div className="lot-quantity">
                                        {lot.collected.toLocaleString()} / {lot.quantity.toLocaleString()} tons
                                      </div>
                                    </div>

                                    {lot.yards && lot.yards.map((yard, yardIndex) => (
                                      <div key={yardIndex} className="yard-card">
                                        <div className="yard-header">
                                          <div>
                                            <h4 className="yard-name">{yard.name}</h4>
                                            <p className="yard-dates">{yard.collectionDates}</p>
                                            {yard.due && (
                                              <p className="yard-overdue">⚠ {yard.due} Overdue</p>
                                            )}
                                          </div>
                                          <div className="yard-progress-badge">
                                            {yard.progress.toFixed(0)}%
                                          </div>
                                        </div>

                                        {featureFlags.showProgressBars && (
                                          <div className="yard-progress-container">
                                            <div className="yard-progress-bar">
                                              <div
                                                className={`yard-progress-fill ${yard.status}`}
                                                style={{ width: `${yard.progress}%` }}
                                              />
                                            </div>
                                            <span className="yard-quantity">
                                              {yard.collected.toLocaleString()} / {yard.planned.toLocaleString()} tons
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ))
                              ) : (
                                <div className="no-data">
                                  <Package size={48} className="no-data-icon" />
                                  <p>No detailed lot information available for this site.</p>
                                </div>
                              )}

                              {featureFlags.showExportButtons && (
                                <div className="action-buttons">
                                  <button className="action-button action-button-primary">
                                    <FileText size={16} />
                                    Generate Report
                                  </button>
                                  <button className="action-button action-button-secondary">
                                    <Download size={16} />
                                    Export to Excel
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Feature Flags Settings Panel */}
                      {showFlagSettings && (
                        <div className="feature-flags-overlay" onClick={() => this.setState({ showFlagSettings: false })}>
                          <div className="feature-flags-panel" onClick={(e) => e.stopPropagation()}>
                            <div className="feature-flags-header">
                              <h3>Feature Flags</h3>
                              <button onClick={() => this.setState({ showFlagSettings: false })} className="close-button-small">
                                <X size={20} />
                              </button>
                            </div>
                            <div className="feature-flags-body">
                              {Object.keys(featureFlags).map(flag => (
                                <div key={flag} className="flag-item">
                                  <label className="flag-label">
                                    {flag.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                  </label>
                                  <button
                                    onClick={() => this.toggleFeatureFlag(flag)}
                                    className={`flag-toggle ${featureFlags[flag] ? 'active' : ''}`}
                                  >
                                    <span className="toggle-slider"></span>
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="feature-flags-footer">
                              <button onClick={this.resetFeatureFlags} className="reset-button">
                                Reset to Defaults
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default LoadingProgressDashboard;